/**
 * Validation utility functions for Exit Form
 * All validation functions return null if valid, or an error message string if invalid
 */

/**
 * Validates PRAN Number (12 digits required)
 */
export const validatePranNumber = (value) => {
  if (!value || value.trim() === '') {
    return 'PRAN Number is required';
  }
  if (!/^\d{12}$/.test(value)) {
    return 'PRAN Number must be exactly 12 digits';
  }
  return null;
};

/**
 * Validates Aadhar Number (12 digits required)
 */
export const validateAadharNumber = (value) => {
  if (!value || value.trim() === '') {
    return 'Aadhar Number is required';
  }
  if (!/^\d{12}$/.test(value)) {
    return 'Aadhar Number must be exactly 12 digits';
  }
  return null;
};

/**
 * Validates PAN Card Number (Format: ABCDE1234F)
 */
export const validatePanCardNumber = (value) => {
  if (!value || value.trim() === '') {
    return 'PAN Card Number is required';
  }
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase())) {
    return 'Invalid PAN Card format (e.g., ABCDE1234F)';
  }
  return null;
};

/**
 * Validates Mobile Number (10 digits required)
 */
export const validateMobileNo = (value) => {
  if (!value || value.trim() === '') {
    return 'Mobile Number is required';
  }
  if (!/^\d{10}$/.test(value)) {
    return 'Mobile Number must be exactly 10 digits';
  }
  return null;
};

/**
 * Validates Alternate Mobile Number (10 digits, optional)
 */
export const validateAlternateMobileNo = (value) => {
  if (value && value.trim() !== '' && !/^\d{10}$/.test(value)) {
    return 'Alternate Mobile Number must be exactly 10 digits';
  }
  return null;
};

/**
 * Validates Email ID
 */
export const validateEmailId = (value) => {
  if (!value || value.trim() === '') {
    return 'Email ID is required';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return 'Invalid email format';
  }
  return null;
};

/**
 * Generic required field validator
 */
export const validateRequiredField = (value, fieldName) => {
  // Handle null, undefined, or non-string values
  if (value === null || value === undefined) {
    return `${fieldName} is required`;
  }
  
  // Convert to string if not already
  const strValue = typeof value === 'string' ? value : String(value);
  if (!strValue || strValue.trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Validates Date of Birth (Age should not be more than 60)
 */
export const validateDOBPran = (value) => {
  if (!value || value.trim() === '') {
    return 'Date of Birth is required';
  }
  const birthDate = new Date(value);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  if (age > 60) {
    return "You can't fill this form if your age is above 60";
  }
  return null;
};

/**
 * Calculates age from date of birth
 * Supports both DD-MM-YYYY and YYYY-MM-DD formats, as well as ISO format
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  
  let birthDate;
  const dateStr = String(dateOfBirth).trim();
  
  // Check if date is in DD-MM-YYYY format
  const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
  if (ddmmyyyyPattern.test(dateStr)) {
    // Convert DD-MM-YYYY to YYYY-MM-DD for Date parsing
    const parts = dateStr.split('-');
    const year = parts[2];
    const month = parts[1];
    const day = parts[0];
    birthDate = new Date(`${year}-${month}-${day}`);
  } else {
    // Handle ISO format (YYYY-MM-DDTHH:mm:ss) or YYYY-MM-DD
    let datePart = dateStr;
    if (dateStr.includes('T')) {
      datePart = dateStr.split('T')[0];
    }
    birthDate = new Date(datePart);
  }
  
  // Check if date is valid
  if (isNaN(birthDate.getTime())) {
    console.error('Invalid date format:', dateOfBirth);
    return 0;
  }
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age >= 0 ? age : 0;
};

/**
 * Validates Bank Account Number
 */
export const validateBankAccountNumber = (value) => {
  if (!value || value.trim() === '') {
    return 'Bank Account Number is required';
  }
  if (!/^\d+$/.test(value)) {
    return 'Bank Account Number must contain only digits';
  }
  if (value.length < 9 || value.length > 18) {
    return 'Bank Account Number must be between 9 and 18 digits';
  }
  return null;
};

/**
 * Validates IFSC Code (Format: ABCD0123456)
 */
export const validateIFSCCode = (value) => {
  if (!value || value.trim() === '') {
    return 'IFSC Code is required';
  }
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase())) {
    return 'Invalid IFSC Code format (e.g., ABCD0123456)';
  }
  return null;
};

/**
 * Validates PRCorpusLumpsum (Max 20%)
 */
export const validatePRCorpusLumpsum = (value) => {
  // Convert to string and check if empty - allow empty input
  const strValue = value !== null && value !== undefined ? String(value) : '';
  if (!strValue || strValue.trim() === '') {
    return null; // Allow empty input
  }
  const numValue = parseFloat(strValue);
  if (isNaN(numValue)) {
    return 'Lump Sum Percentage must be a number';
  }
  if (numValue < 0) {
    return 'Lump Sum Percentage cannot be negative';
  }
  if (numValue > 20) {
    return 'Lump Sum Percentage cannot exceed 20%';
  }
  return null;
};

/**
 * Validates NPSCorpus (Must be positive number)
 */
export const validateNPSCorpus = (value) => {
  // Convert to string and check if empty
  const strValue = value !== null && value !== undefined ? String(value) : '';
  if (!strValue || strValue.trim() === '') {
    return 'NPS Corpus is required';
  }
  const numValue = parseFloat(strValue);
  if (isNaN(numValue)) {
    return 'NPS Corpus must be a number';
  }
  if (numValue <= 0) {
    return 'NPS Corpus must be greater than 0';
  }
  return null;
};

/**
 * Validates Withdrawal PR (Withdrawal Percentage)
 */
export const validateWithdrawalPR = (value) => {
  // Convert to string and check if empty
  const strValue = value !== null && value !== undefined ? String(value) : '';
  if (!strValue || strValue.trim() === '') {
    return 'Withdrawal Percentage is required';
  }
  const numValue = parseFloat(strValue);
  if (isNaN(numValue)) {
    return 'Withdrawal Percentage must be a number';
  }
  if (numValue <= 0) {
    return 'Withdrawal Percentage must be greater than 0';
  }
  if (numValue > 20) {
    return 'Withdrawal Percentage must be 20% or less';
  }
  return null;
};

/**
 * Validates Withdrawal Amount (Must be positive number)
 */
export const validateWithdrawalAmount = (value) => {
  // Convert to string and check if empty
  const strValue = value !== null && value !== undefined ? String(value) : '';
  if (!strValue || strValue.trim() === '') {
    return 'Withdrawal Amount is required';
  }
  const numValue = parseFloat(strValue);
  if (isNaN(numValue)) {
    return 'Withdrawal Amount must be a number';
  }
  if (numValue <= 0) {
    return 'Withdrawal Amount must be greater than 0';
  }
  return null;
};

/**
 * Validates Annuity Value (Must be positive number)
 */
export const validateAnnuityValue = (value) => {
  // Convert to string and check if empty
  const strValue = value !== null && value !== undefined ? String(value) : '';
  if (!strValue || strValue.trim() === '') {
    return 'Annuity Value is required';
  }
  const numValue = parseFloat(strValue);
  if (isNaN(numValue)) {
    return 'Annuity Value must be a number';
  }
  if (numValue <= 0) {
    return 'Annuity Value must be greater than 0';
  }
  return null;
};

/**
 * Validates Work Sector DOB (Date format and age)
 */
export const validateWorkSectorDOB = (value) => {
  const strValue = value !== null && value !== undefined ? String(value) : '';
  if (!strValue || strValue.trim() === '') {
    return 'Date of Birth is required';
  }

  let day, month, year;

  // YYYY-MM-DD format
  const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  // DD-MM-YYYY format
  const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;

  if (yyyymmddPattern.test(strValue)) {
    const parts = strValue.split('-');
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10) - 1;
    day = parseInt(parts[2], 10);
  } else if (ddmmyyyyPattern.test(strValue)) {
    const parts = strValue.split('-');
    day = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10) - 1;
    year = parseInt(parts[2], 10);
  } else {
    return 'Invalid date format (DD-MM-YYYY)';
  }

  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return 'Invalid date';
  }

  const birthDate = new Date(year, month, day);
  const isValidDate = birthDate.getDate() === day &&
                      birthDate.getMonth() === month &&
                      birthDate.getFullYear() === year &&
                      month >= 0 && month <= 11 &&
                      day >= 1 && day <= 31;

  if (!isValidDate) {
    return 'Invalid date format (DD-MM-YYYY)';
  }

  const today = new Date();
  if (birthDate > today) {
    return 'Date of Birth cannot be in the future';
  }

  return null;
};

/**
 * Check if all Section A fields are valid
 */
export const checkSectionAValidity = (formData, errors) => {
  const isValid = 
    !errors.PranNumber &&
    !errors.SubscriberName &&
    !errors.OrganizationName &&
    !errors.PranName &&
    !errors.Subscribergendername &&
    !errors.FatherName &&
    !errors.AadharNumber &&
    !errors.PanCardNumber &&
    !errors.MobileNo &&
    !errors.AlternateMobileNo &&
    !errors.EmailId &&
    !errors.SubscriberAddress &&
    !errors.DOBPran &&
    formData.PranNumber &&
    formData.SubscriberName &&
    formData.OrganizationName &&
    formData.PranName &&
    formData.Subscribergendername &&
    formData.FatherName &&
    formData.AadharNumber &&
    formData.PanCardNumber &&
    formData.MobileNo &&
    formData.EmailId &&
    formData.SubscriberAddress &&
    formData.DOBPran;
  
  return isValid;
};

/**
 * Check if all Section B fields are valid
 */
export const checkSectionBValidity = (formData, errors) => {
  const isValid = 
    !errors.BankAccountNumber &&
    !errors.BankName &&
    !errors.BankBranchAddress &&
    !errors.IFSCCode &&
    formData.BankAccountNumber &&
    formData.BankName &&
    formData.BankBranchAddress &&
    formData.IFSCCode;
  
  return isValid;
};

/**
 * Check if all Section C fields are valid
 */
export const checkSectionCValidity = (formData, errors) => {
  // Check if PRCorpusLumpsum is 0 - if so, allow WithdrawalPR, WithdrawalAmount, and AnnuityValue to be 0
  const prCorpusLumpsumValue = parseFloat(formData.PRCorpusLumpsum) || 0;
  const isZeroWithdrawal = prCorpusLumpsumValue === 0;
  
  // Base validation for required fields
  const baseFieldsValid = 
    !errors.PRCorpusLumpsum &&
    !errors.WorkSectorName &&
    !errors.AnnuitySchemeName &&
    !errors.AnnuityFrequencySchemeName &&
    !errors.WorkSectorDOB &&
    !errors.NPSCorpus &&
    formData.PRCorpusLumpsum !== '' &&
    formData.PRCorpusLumpsum !== null &&
    formData.PRCorpusLumpsum !== undefined &&
    formData.WorkSectorName &&
    formData.AnnuitySchemeName &&
    formData.AnnuityFrequencySchemeName &&
    formData.WorkSectorDOB &&
    formData.NPSCorpus;
  
  // If withdrawal is 0, allow WithdrawalPR, WithdrawalAmount, and AnnuityValue to be 0
  if (isZeroWithdrawal) {
    const isValid = 
      baseFieldsValid &&
      !errors.WithdrawalPR &&
      !errors.WithdrawalAmount &&
      !errors.AnnuityValue &&
      formData.WithdrawalPR !== '' &&
      formData.WithdrawalPR !== null &&
      formData.WithdrawalPR !== undefined;
    
    return isValid;
  }
  
  // For non-zero withdrawal, require all fields
  const isValid = 
    baseFieldsValid &&
    !errors.WithdrawalPR &&
    !errors.WithdrawalAmount &&
    !errors.AnnuityValue &&
    formData.WithdrawalPR &&
    formData.WithdrawalAmount &&
    formData.AnnuityValue;
  
  return isValid;
};

